/**
 * HeroStickyHeader - Sticky header for hero layout
 *
 * A sticky bar that includes:
 * - Event title, date/time, location (top row)
 * - Tab navigation (below)
 * - Price and Buy tickets CTA (on desktop, in sidebar card)
 *
 * Combines the simplified header info with tab navigation.
 */
import { useMemo, useState, useEffect, Fragment } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "@shared/utils/internationalization";
import moment from "moment-timezone";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { StickySection } from "@shared/components/application/layout/StickySection/StickySection.web";
import { PageContentWrapper } from "@shared/components/application/layout/PageContentWrapper/PageContentWrapper.web";
import { common } from "@shared/constants";
import breakpoints from "@shared/theme/props/breakpoints";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { useTimer } from "@ui-kit/feedback/timer/useTimer";
import useDetailPageTabHeight from "@app-contexts/public/features/event-detail/hooks/useDetailPageTabHeight";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { TabBar as Tabs } from "@ui-kit/disclosure/TabBar/TabBar.web";
import CheckoutButton from "@shared/components/application/form/CheckoutButton/CheckoutButton.web";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import useRedirect from "@shared/hooks/useRedirect";
import type { PageData } from "../../../adapters/types";
import { swiperContainerStyles } from "../../shared-styles";
import { DetailPageButton } from "../../../components/shared/DetailPageButton.web";
import { DetailPageShareAndFavoriteButtons } from "../../../components/shared/DetailPageShareAndFavoriteButtons.web";

const { xs, md } = breakpoints;
const { headerTabsId } = common.domElementIds;

interface TabButtonProps {
  isSelected: boolean;
  onClick: () => void;
  title: string;
}

const TabButton = ({ isSelected, onClick, title }: TabButtonProps) => (
  <Box
    as="button"
    role="button"
    tabIndex={0}
    onClick={onClick}
    data-testid={`${title}-tab`}
    bg="transparent"
    border="none"
    p="none"
    pb="sm"
    mx="md"
    fontWeight="normal"
    cursor="pointer"
    borderBottom={isSelected ? "3px solid" : "3px solid transparent"}
    borderBottomColor={isSelected ? "primary.solid" : "transparent"}
    _hover={{
      bg: "transparent",
    }}
    _focusVisible={{
      outline: "2px solid",
      outlineColor: "blue.focusRing",
      borderRadius: "1px",
    }}
    // eslint-disable-next-line showpass-custom/prefer-semantic-props
    css={{
      "&:first-of-type": {
        marginLeft: 0,
      },
    }}
  >
    <Text fontSize={{ base: "xs", sm: "sm" }} color="fg">
      {title}
    </Text>
  </Box>
);

interface HeroStickyHeaderProps {
  data: PageData;
}

export function HeroStickyHeader({ data }: HeroStickyHeaderProps) {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { hasItems, totalItemQuantity } = usePublicBasket();
  const tabHeight = useDetailPageTabHeight(hasItems);
  const [isMobile] = useMediaQuery(`(max-width: ${xs - 1}px)`);
  const [isTabletMedia] = useMediaQuery(
    `(min-width: ${xs}px) and (max-width: ${md - 1}px)`
  );
  const { redirect } = useRedirect();
  const navigateToCheckout = () => redirect(PUBLIC_ROUTES.CHECKOUT.ROOT);

  const { stickyTabs, ui, rawData, pageType, isAttraction, header, modals } =
    data;

  SwiperCore.use([Navigation]);
  const [swiper, setSwiper] = useState<SwiperCore | undefined>();

  const visibleTabs = stickyTabs.tabs.filter((tab) => tab.visible);

  const handleTabClick = (tabId: number, index: number) => {
    const tabInfo = visibleTabs.find((tab) => tab.id === tabId);

    if (tabInfo?.hash) {
      stickyTabs.onTabChange(tabId);
      router.replace({ hash: tabInfo.hash }, undefined, { shallow: true });

      if ((isMobile || isTabletMedia) && visibleTabs.length > 2) {
        if (swiper && index === 1) {
          swiper.slideTo(0);
        } else if (swiper && index >= 2) {
          swiper.slideTo(index - 2);
        }
      }
    } else if (tabInfo?.onClick) {
      tabInfo.onClick();
    }
  };

  // Price range for display
  const priceRangeString = useMemo(() => {
    if (pageType === "event" && rawData.event) {
      return rawData.event.no_ticket_types
        ? t("Free")
        : getPriceRange({
            items: rawData.event.ticket_types,
            allInPricing: Boolean(
              rawData.event.venue?.all_in_pricing_is_enabled
            ),
            venueCurrency: rawData.event.venue?.currency ?? "CAD",
            shouldRound: false,
          });
    }

    if (pageType === "membership" && rawData.membership) {
      return getPriceRange({
        items: rawData.membership.membership_levels,
        allInPricing: Boolean(
          rawData.membership.venue?.all_in_pricing_is_enabled
        ),
        venueCurrency: rawData.membership.venue?.currency ?? "CAD",
        shouldRound: false,
      });
    }

    return "";
  }, [rawData.event, rawData.membership, pageType, t]);

  const hidePriceText = !priceRangeString || isAttraction;

  // Timer for sale start
  const eventSaleStartsOn = rawData.event?.event_sale_starts_on;
  const saleStartsOnTz =
    eventSaleStartsOn && rawData.event?.timezone
      ? moment.tz(eventSaleStartsOn, rawData.event.timezone).toDate()
      : null;
  const [timeRemaining] = useTimer(saleStartsOnTz ?? new Date());

  // Hydration-safe date comparison
  const [saleStartsOnPassed, setSaleStartsOnPassed] = useState(true);
  useEffect(() => {
    if (eventSaleStartsOn) {
      setSaleStartsOnPassed(
        Date.now() >= new Date(eventSaleStartsOn).valueOf()
      );
    }
  }, [eventSaleStartsOn]);

  // Check if this is a free item
  const isFreeItem =
    pageType === "event" ? rawData.event?.no_ticket_types : false;

  // Filter to only visible statuses from the adapter
  const visibleStatuses = header.statuses.filter((status) => status.visible);

  const showCartItemCount = isAttraction && hasItems;

  // Don't render if no title
  if (!header.title) return null;
  if (stickyTabs.selectedTab === undefined) return null;

  return (
    <>
      {/* Header info row - MOBILE ONLY - scrolls away, NOT sticky */}
      <Box bg="bg" display={{ base: "block", md: "none" }}>
        <PageContentWrapper>
          <Flex direction="column" pt="sm" gap="md">
            {/* Status badges, Title and info - full width */}
            <Flex direction="column" gap="2xs" minWidth={0}>
              {/* Status badges - above title */}
              {!ui.isLoading && visibleStatuses.length > 0 && (
                <Flex gap="xs" flexWrap="wrap">
                  {visibleStatuses.map((status) => (
                    <Fragment key={status.id}>{status.element}</Fragment>
                  ))}
                </Flex>
              )}

              {/* Event title */}
              <Text
                fontWeight="bold"
                fontSize="xl"
                color="fg"
                lineClamp={2}
                overflow="hidden"
                textOverflow="ellipsis"
                data-testid="sticky-header-title-mobile"
              >
                {header.title}
              </Text>

              {/* Subtitle and organizer - stacked */}
              <Flex
                direction="column"
                gap="none"
                color="fg.muted"
                fontSize="xs"
              >
                {header.subtitle && (
                  <Text lineClamp={1}>{header.subtitle}</Text>
                )}
                {header.organizer.name && (
                  <Link
                    color="fg.muted"
                    href={`/o/${header.organizer.slug}`}
                    fontSize="xs"
                  >
                    {header.organizer.name}
                  </Link>
                )}
              </Flex>
            </Flex>

            {/* Share/Save icons (left) and Price (right) - Mobile */}
            <Flex
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              data-testid="sticky-header-actions-mobile"
            >
              <DetailPageShareAndFavoriteButtons
                onShareClick={modals.share.onOpen}
                favoriteData={header.favoriteData}
              />
              {!isAttraction && !hidePriceText && (
                <Flex
                  alignItems="center"
                  gap="md"
                  data-testid="sticky-header-price-mobile"
                >
                  <Text fontSize="sm" color="fg.muted">
                    {t("Price")}
                  </Text>
                  <Text
                    fontWeight="bold"
                    fontSize="sm"
                    color="fg"
                    whiteSpace="nowrap"
                  >
                    {priceRangeString}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </PageContentWrapper>
      </Box>

      {/* STICKY SECTION */}
      <StickySection
        top={tabHeight}
        bg="bg"
        zIndex="navbarTabs"
        borderBottom={!ui.isReactNativeApp ? "1px solid" : "0px solid"}
        borderBottomColor="border"
        noPadding
        id="sticky-header"
      >
        {/* Header info row - DESKTOP ONLY - inside sticky */}
        <Box display={{ base: "none", md: "block" }}>
          <PageContentWrapper>
            <Flex
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              py="sm"
              gap="md"
            >
              {/* Left side: Status badges, Title and info */}
              <Flex direction="column" minWidth={0} gap="2xs" mr="lg">
                {/* Status badges - above title */}
                {!ui.isLoading && visibleStatuses.length > 0 && (
                  <Flex gap="xs" flexWrap="wrap">
                    {visibleStatuses.map((status) => (
                      <Fragment key={status.id}>{status.element}</Fragment>
                    ))}
                  </Flex>
                )}

                {/* Event title */}
                <Text
                  fontWeight="bold"
                  fontSize={{ sm: "xl", lg: "4xl" }}
                  color="fg"
                  lineClamp={2}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  data-testid="sticky-header-title"
                >
                  {header.title}
                </Text>

                {/* Subtitle and organizer - stacked */}
                <Flex
                  direction="column"
                  gap="none"
                  color="fg.muted"
                  fontSize={{ sm: "sm", lg: "md" }}
                >
                  {header.subtitle && (
                    <Text lineClamp={1}>{header.subtitle}</Text>
                  )}
                  {header.organizer.name && (
                    <Link
                      color="fg.muted"
                      href={`/o/${header.organizer.slug}`}
                      fontSize={{ sm: "sm", lg: "md" }}
                    >
                      {header.organizer.name}
                    </Link>
                  )}
                </Flex>
              </Flex>
              <Flex direction="column" gap="sm">
                {/* Right side: Price and CTA */}
                <Flex
                  direction="row"
                  alignItems="center"
                  gap="md"
                  flexShrink={0}
                >
                  {/* Price display */}
                  {!isAttraction && (
                    <Flex
                      direction="column"
                      alignItems="flex-end"
                      hidden={hidePriceText}
                      data-testid="sticky-header-price"
                      flexShrink={0}
                    >
                      <Text fontSize="sm" color="fg.muted">
                        {t("Price")}
                      </Text>
                      <Text
                        fontWeight="bold"
                        fontSize="md"
                        color="fg"
                        whiteSpace="nowrap"
                      >
                        {priceRangeString}
                      </Text>
                    </Flex>
                  )}
                  {/* CTA Button */}
                  {isAttraction ? (
                    <CheckoutButton
                      isDisabled={!showCartItemCount}
                      onCheckout={navigateToCheckout}
                      quantityToDisplay={totalItemQuantity}
                      customVerbiage={t("Checkout").toUpperCase()}
                    />
                  ) : (
                    <DetailPageButton
                      saleStartsOnPassed={saleStartsOnPassed}
                      eventData={rawData.event}
                      membershipGroupData={rawData.membership}
                      openModal={stickyTabs.cta.onClick}
                      props={{
                        "data-testid": "sticky-header-cta-button",
                        colorPalette: "secondary",
                        size: "lg",
                        display:
                          pageType === "event" &&
                          eventSaleStartsOn &&
                          timeRemaining > 0
                            ? "none"
                            : "flex",
                        hidden: isFreeItem,
                      }}
                    />
                  )}
                </Flex>
                <DetailPageShareAndFavoriteButtons
                  onShareClick={modals.share.onOpen}
                  favoriteData={header.favoriteData}
                  flexProps={{
                    alignSelf: "flex-end",
                  }}
                />
              </Flex>
            </Flex>
          </PageContentWrapper>
        </Box>

        {/* CTA Button row - MOBILE ONLY */}
        {!isAttraction && (
          <PageContentWrapper>
            <Flex
              py="sm"
              justifyContent="flex-end"
              display={{ base: "flex", md: "none" }}
            >
              <DetailPageButton
                saleStartsOnPassed={saleStartsOnPassed}
                eventData={rawData.event}
                membershipGroupData={rawData.membership}
                openModal={stickyTabs.cta.onClick}
                props={{
                  "data-testid": "sticky-header-cta-button-mobile",
                  colorPalette: "secondary",
                  size: "lg",
                  width: "100%",
                  display:
                    pageType === "event" &&
                    eventSaleStartsOn &&
                    timeRemaining > 0
                      ? "none"
                      : "flex",
                  hidden: isFreeItem,
                }}
              />
            </Flex>
          </PageContentWrapper>
        )}

        {/* Tabs row - below CTA */}
        <PageContentWrapper>
          <Flex bg="bg" w="100%">
            <Tabs
              id={headerTabsId}
              width="100%"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              isManual
              index={stickyTabs.selectedTab}
              css={swiperContainerStyles}
            >
              <IconButton
                iconName="ChevronLeft"
                iconOutline={false}
                iconSize="20px"
                aria-label="Previous tab"
                className="prev-button"
                iconColor="fg"
                marginLeft={{ base: "none", sm: "-10px" }}
                data-testid="prev-nav-button"
                display={swiper ? undefined : "none"}
                _disabled={{ display: "none" }}
              />
              <Swiper
                navigation={{
                  prevEl: ".prev-button",
                  nextEl: ".next-button",
                }}
                allowTouchMove={visibleTabs.length > 3}
                slidesPerView="auto"
                onSwiper={setSwiper}
                initialSlide={
                  stickyTabs.selectedTab === 0 ? 0 : stickyTabs.selectedTab
                }
                watchOverflow
              >
                {visibleTabs.map((tab, index) => (
                  <SwiperSlide key={tab.title}>
                    <Flex alignItems="flex-end" h="element.4xl">
                      <TabButton
                        isSelected={stickyTabs.selectedTab === tab.id}
                        onClick={() => handleTabClick(tab.id, index)}
                        title={tab.title}
                      />
                    </Flex>
                  </SwiperSlide>
                ))}
              </Swiper>
              <IconButton
                iconName="ChevronRight"
                iconOutline={false}
                iconSize="20px"
                aria-label="Next tab"
                iconColor="fg"
                className="next-button"
                data-testid="next-nav-button"
                display={swiper ? undefined : "none"}
                _disabled={{ display: "none" }}
              />
            </Tabs>
          </Flex>
        </PageContentWrapper>
      </StickySection>
    </>
  );
}
